import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ExternalLink, Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/landing/Reveal";
import { api, formatApiError } from "@/lib/api";

export default function Checkout() {
    const navigate = useNavigate();
    const [mode, setMode] = useState("loading");
    const [form, setForm] = useState({ full_name: "", email: "", tradingview_username: "", card: "", expiry: "", cvc: "" });
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        api.get("/checkout/config")
            .then(({ data }) => setMode(data.ready ? "live" : "demo"))
            .catch(() => setMode("demo"));
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const detailsValid =
        form.full_name.trim().length >= 2 &&
        /^\S+@\S+\.\S+$/.test(form.email.trim()) &&
        form.tradingview_username.trim().length >= 2;

    const demoCardValid =
        form.card.replace(/\s/g, "").length >= 12 && form.expiry.trim().length >= 4 && form.cvc.trim().length >= 3;

    const valid = mode === "live" ? detailsValid : detailsValid && demoCardValid;

    const submit = async (e) => {
        e.preventDefault();
        if (!valid || processing) return;
        setProcessing(true);
        const payload = {
            full_name: form.full_name.trim(),
            email: form.email.trim(),
            tradingview_username: form.tradingview_username.trim(),
        };
        try {
            if (mode === "live") {
                const { data } = await api.post("/checkout/create", payload);
                toast.success("Redirecting to secure checkout…");
                window.location.assign(data.checkout_url);
                return;
            }
            const { data: order } = await api.post("/orders", payload);
            await new Promise((r) => setTimeout(r, 1200));
            toast.success("Payment received — order confirmed");
            navigate(`/confirmation/${order.order_id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Could not complete your order. Please try again."));
            setProcessing(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";

    return (
        <div className="min-h-screen bg-[#FAFBFD]" data-testid="checkout-page">
            <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
                    <Link to="/" data-testid="checkout-logo-link">
                        <Logo />
                    </Link>
                    <Link to="/" data-testid="checkout-back-link" className="flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900">
                        <ArrowLeft className="h-4 w-4" /> Back to site
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <p className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700">Step 1 &amp; 2 of 3</p>
                    <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
                        Get ITF on your chart.
                    </h1>
                    <p className="mt-3 max-w-xl text-base text-slate-600">
                        Complete checkout, tell us your TradingView username, and we will activate access on your account —
                        usually within a few hours.
                    </p>
                </motion.div>

                <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-5">
                    <motion.form
                        onSubmit={submit}
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="space-y-6 lg:col-span-3"
                    >
                        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
                            <h2 className="font-display text-base font-bold text-slate-900">Your details</h2>
                            <div className="mt-5 space-y-4">
                                <div>
                                    <label htmlFor="full-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        Full name
                                    </label>
                                    <input id="full-name" data-testid="checkout-fullname-input" className={inputCls} placeholder="Alex Morgan" value={form.full_name} onChange={set("full_name")} autoComplete="name" />
                                </div>
                                <div>
                                    <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        Email address
                                    </label>
                                    <input id="email" type="email" data-testid="checkout-email-input" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} autoComplete="email" />
                                    <p className="mt-1.5 text-xs text-slate-400">Your order confirmation and activation notice go here.</p>
                                </div>
                                <div>
                                    <label htmlFor="tv-username" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        TradingView username
                                    </label>
                                    <input id="tv-username" data-testid="checkout-tradingview-username-input" className={inputCls} placeholder="your_tradingview_username" value={form.tradingview_username} onChange={set("tradingview_username")} />
                                    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-400">
                                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                                        We only need your public username — never your password.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
                            <div className="flex items-center justify-between">
                                <h2 className="font-display text-base font-bold text-slate-900">Payment</h2>
                                <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                    {mode === "loading" ? "Loading…" : mode === "live" ? "Secured by Dodo Payments" : "Demo mode"}
                                </span>
                            </div>
                            {mode === "live" ? (
                                <div className="mt-5">
                                    <p className="text-sm leading-relaxed text-slate-600">
                                        Clicking below takes you to Dodo Payments' secure hosted checkout to complete your
                                        payment of $275.00 USD. Your card details are handled entirely by Dodo Payments —
                                        we never see or store them.
                                    </p>
                                    <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
                                        <ExternalLink className="h-3.5 w-3.5" /> You'll return here automatically after payment.
                                    </p>
                                </div>
                            ) : (
                                <div className="mt-5 space-y-4">
                                    <div>
                                        <label htmlFor="card-number" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                                            Card number
                                        </label>
                                        <input id="card-number" data-testid="checkout-card-number-input" className={inputCls} placeholder="4242 4242 4242 4242" value={form.card} onChange={set("card")} inputMode="numeric" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="card-expiry" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                                                Expiry
                                            </label>
                                            <input id="card-expiry" data-testid="checkout-card-expiry-input" className={inputCls} placeholder="12 / 28" value={form.expiry} onChange={set("expiry")} />
                                        </div>
                                        <div>
                                            <label htmlFor="card-cvc" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                                                CVC
                                            </label>
                                            <input id="card-cvc" data-testid="checkout-card-cvc-input" className={inputCls} placeholder="123" value={form.cvc} onChange={set("cvc")} inputMode="numeric" />
                                        </div>
                                    </div>
                                    <p className="text-xs leading-relaxed text-slate-400">
                                        Demo mode — the live Dodo Payments checkout activates automatically as soon as the
                                        store keys are connected. No real payment is processed in demo mode.
                                    </p>
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={!valid || processing || mode === "loading"}
                            data-testid="checkout-submit-button"
                            className="group flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 py-4 text-base font-semibold text-white shadow-lg shadow-emerald-600/25 transition-all duration-200 hover:bg-emerald-700 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
                        >
                            {processing ? (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin" /> {mode === "live" ? "Opening secure checkout…" : "Processing payment…"}
                                </>
                            ) : (
                                <>
                                    <Lock className="h-4 w-4" /> {mode === "live" ? "Continue to secure payment — $275.00" : "Pay $275.00 — 12 months of ITF"}
                                </>
                            )}
                        </button>

                        <p className="text-xs leading-relaxed text-slate-500">
                            <span className="font-semibold text-slate-700">Payment processing and Merchant of Record:</span>{" "}
                            Dodo Payments is our authorized reseller and Merchant of Record. Dodo Payments processes your
                            payment, calculates and collects applicable taxes, and issues your receipt. This is a one-time
                            payment of $275 USD for 12 months of access — no auto-renewal. By purchasing you agree to our{" "}
                            <Link to="/terms" className="font-semibold text-emerald-700 hover:text-emerald-800">Terms &amp; Conditions</Link>,{" "}
                            <Link to="/refund-policy" className="font-semibold text-emerald-700 hover:text-emerald-800">Refund Policy</Link> and{" "}
                            <Link to="/privacy" className="font-semibold text-emerald-700 hover:text-emerald-800">Privacy Policy</Link>.
                        </p>
                        <p className="text-center font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">
                            One payment · No auto-renewal · All sales final
                        </p>
                    </motion.form>

                    <motion.aside
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="lg:col-span-2"
                    >
                        <div className="sticky top-8 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-7" data-testid="checkout-order-summary">
                            <h2 className="font-display text-base font-bold text-slate-900">Order summary</h2>
                            <div className="mt-5 rounded-xl bg-slate-50 p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="font-display text-sm font-bold text-slate-900">ITF Annual Access</p>
                                        <p className="mt-0.5 text-xs text-slate-500">12 months · all features</p>
                                    </div>
                                    <span className="font-display text-lg font-extrabold text-slate-900">$275</span>
                                </div>
                            </div>
                            <div className="mt-4 space-y-2 text-xs text-slate-500">
                                <div className="flex justify-between"><span>Per month</span><span className="font-mono">$22.92</span></div>
                                <div className="flex justify-between"><span>Per day</span><span className="font-mono">$0.75</span></div>
                                <div className="flex justify-between border-t border-slate-100 pt-2 text-sm font-semibold text-slate-900">
                                    <span>Total today</span><span>$275.00</span>
                                </div>
                            </div>
                            <ul className="mt-5 space-y-2.5 border-t border-slate-100 pt-5">
                                {["Activated on your TradingView account", "Confirmation email with next steps", "Usually live within a few hours", "Support: support@getitf.xyz"].map((t) => (
                                    <li key={t} className="flex items-start gap-2 text-xs text-slate-600">
                                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={3} />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.aside>
                </div>
            </main>
        </div>
    );
}
